import { TSharedDTO } from '~/types/DTO/shared';
import { LegalQuestionsForm } from './LegalQuestions.schema';

const isProductionEnvironment =
  import.meta.env.MODE === 'production' ||
  import.meta.env.MODE === 'internal-prod';

// Question and answer IDs are defined by the client assessment questionnaire contract.
// They must match the values seeded in the svc-client-assessment service.
// If any ID changes, the isBelgiumClientAssessmentInSync check will catch the drift at runtime.
// Answer IDs differ between test and production environments.
export const BelgiumClientAssessment = {
  IsMarried: {
    questionId: 200,
    answerYes: {
      id: isProductionEnvironment ? 200 : 131,
      name: 'YES',
    },
    answerNo: {
      id: isProductionEnvironment ? 201 : 132,
      name: 'NO',
    },
  },
  HasPrenuptialAgreement: {
    questionId: 201,
    answerYes: {
      id: isProductionEnvironment ? 202 : 164,
      name: 'YES',
    },
    answerNo: {
      id: isProductionEnvironment ? 203 : 165,
      name: 'NO',
    },
  },
  SpouseName: { questionId: 202 },
  SpouseAddress: { questionId: 203 },
  SpouseSSN: { questionId: 204 },
  HasOtherDebt: {
    questionId: 205,
    answerYes: {
      id: isProductionEnvironment ? 204 : 168,
      name: 'YES',
    },
    answerNo: {
      id: isProductionEnvironment ? 205 : 166,
      name: 'NO',
    },
  },
  OtherDebtAmount: {
    questionId: 206,
    answer1000To50000: {
      id: isProductionEnvironment ? 206 : 167,
      name: '1000 - 50000',
    },
    answer50001To100000: {
      id: isProductionEnvironment ? 207 : 197,
      name: '50001 - 100000',
    },
    answer100001To250000: {
      id: isProductionEnvironment ? 208 : 198,
      name: '100001 - 250000',
    },
    answer250001To500000: {
      id: isProductionEnvironment ? 209 : 199,
      name: '250001 - 500000',
    },
    answer500000Plus: {
      id: isProductionEnvironment ? 210 : 200,
      name: '500000+',
    },
  },
};

const {
  IsMarried,
  HasPrenuptialAgreement,
  HasOtherDebt,
  OtherDebtAmount,
  SpouseName,
  SpouseAddress,
  SpouseSSN,
} = BelgiumClientAssessment;

export const isBelgiumClientAssessmentInSync = (
  data: TSharedDTO['getKYCQuestionsAndAnswersRes'] | undefined
): boolean => {
  if (!data) {
    return false;
  }

  const questionAnswerIds = new Map(
    data.questions.map((question) => [
      question.id,
      new Set(question.answers.map((answer) => answer.id)),
    ])
  );

  const verifyClientAssessmentAnswersExist = (
    questionId: number,
    ...answerIds: number[]
  ) => {
    const answerSet = questionAnswerIds.get(questionId);

    if (!answerSet) {
      return false;
    }

    return answerIds.every((id) => answerSet.has(id));
  };

  return (
    verifyClientAssessmentAnswersExist(
      IsMarried.questionId,
      IsMarried.answerYes.id,
      IsMarried.answerNo.id
    ) &&
    verifyClientAssessmentAnswersExist(
      HasPrenuptialAgreement.questionId,
      HasPrenuptialAgreement.answerYes.id,
      HasPrenuptialAgreement.answerNo.id
    ) &&
    verifyClientAssessmentAnswersExist(
      HasOtherDebt.questionId,
      HasOtherDebt.answerYes.id,
      HasOtherDebt.answerNo.id
    ) &&
    verifyClientAssessmentAnswersExist(
      OtherDebtAmount.questionId,
      OtherDebtAmount.answer1000To50000.id,
      OtherDebtAmount.answer50001To100000.id,
      OtherDebtAmount.answer100001To250000.id,
      OtherDebtAmount.answer250001To500000.id,
      OtherDebtAmount.answer500000Plus.id
    ) &&
    questionAnswerIds.has(SpouseName.questionId) &&
    questionAnswerIds.has(SpouseAddress.questionId) &&
    questionAnswerIds.has(SpouseSSN.questionId)
  );
};

export const otherDebtAmountNameById: Record<number, string> = {
  [OtherDebtAmount.answer1000To50000.id]:
    OtherDebtAmount.answer1000To50000.name,
  [OtherDebtAmount.answer50001To100000.id]:
    OtherDebtAmount.answer50001To100000.name,
  [OtherDebtAmount.answer100001To250000.id]:
    OtherDebtAmount.answer100001To250000.name,
  [OtherDebtAmount.answer250001To500000.id]:
    OtherDebtAmount.answer250001To500000.name,
  [OtherDebtAmount.answer500000Plus.id]: OtherDebtAmount.answer500000Plus.name,
};

export const belgiumClientAssessmentQuestionTranslationById: Record<
  number,
  string
> = {
  [IsMarried.questionId]: 'Onboarding.LegalQuestionsStepAreYouMarried',
  [HasPrenuptialAgreement.questionId]:
    'Onboarding.LegalQuestionsStepPrenuptialAgreement',
  [SpouseName.questionId]: 'Onboarding.LegalQuestionsStepSpouseName',
  [SpouseAddress.questionId]: 'Onboarding.LegalQuestionsStepSpouseAddress',
  [SpouseSSN.questionId]: 'Onboarding.LegalQuestionsStepSpouseSSN',
  [HasOtherDebt.questionId]: 'Onboarding.LegalQuestionsStepOtherDebt',
  [OtherDebtAmount.questionId]: 'Onboarding.LegalQuestionsStepDebtAmount',
};

export const belgiumClientAssessmentAnswerTranslationById: Record<
  number,
  string
> = {
  [IsMarried.answerYes.id]: 'Onboarding.LegalQuestionsStepIAmMarried',
  [IsMarried.answerNo.id]: 'Onboarding.LegalQuestionsStepIAmNotMarried',
  [HasPrenuptialAgreement.answerYes.id]: 'Onboarding.YesIDo',
  [HasPrenuptialAgreement.answerNo.id]: 'Onboarding.NoIDoNot',
  [HasOtherDebt.answerYes.id]: 'Onboarding.LegalQuestionsStepOtherDebtYes',
  [HasOtherDebt.answerNo.id]: 'Onboarding.LegalQuestionsStepOtherDebtNo',
};

export const mapFormDataToPostClientAssessmentAnswersBody = (
  formData: LegalQuestionsForm
): TSharedDTO['postKYCAnswersReq'] => {
  const questionsAndAnswers: TSharedDTO['postKYCAnswersReq']['questionsAndAnswers'] = [
    {
      questionId: IsMarried.questionId,
      answers: [
        {
          answerId: formData.isMarried,
          answer:
            formData.isMarried === IsMarried.answerYes.id
              ? IsMarried.answerYes.name
              : IsMarried.answerNo.name,
        },
      ],
    },
    {
      questionId: HasOtherDebt.questionId,
      answers: [
        {
          answerId: formData.hasOtherDebt,
          answer:
            formData.hasOtherDebt === HasOtherDebt.answerYes.id
              ? HasOtherDebt.answerYes.name
              : HasOtherDebt.answerNo.name,
        },
      ],
    },
  ];

  if ('hasPrenuptialAgreement' in formData) {
    questionsAndAnswers.push({
      questionId: HasPrenuptialAgreement.questionId,
      answers: [
        {
          answerId: formData.hasPrenuptialAgreement,
          answer:
            formData.hasPrenuptialAgreement ===
            HasPrenuptialAgreement.answerYes.id
              ? HasPrenuptialAgreement.answerYes.name
              : HasPrenuptialAgreement.answerNo.name,
        },
      ],
    });
  }

  if ('spouse' in formData) {
    questionsAndAnswers.push(
      {
        questionId: SpouseName.questionId,
        answers: [
          {
            answer: `${formData.spouse.firstName} ${formData.spouse.lastName}`,
          },
        ],
      },
      {
        questionId: SpouseAddress.questionId,
        answers: [{ answer: formData.spouse.address }],
      },
      {
        questionId: SpouseSSN.questionId,
        answers: [{ answer: formData.spouse.personalNumber }],
      }
    );
  }

  if ('otherDebtAmount' in formData) {
    questionsAndAnswers.push({
      questionId: OtherDebtAmount.questionId,
      answers: [
        {
          answerId: formData.otherDebtAmount,
          answer: otherDebtAmountNameById[formData.otherDebtAmount],
        },
      ],
    });
  }

  return { questionsAndAnswers };
};
